import PnlChartPopup from "components/Dashboard/TrendChart/TrendChartContent/PnlChartPopup/PnlChartPopup";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { DASHBOARD_CARD_PROPS } from "../../dashboardPagesContentUtils";
import TrendChart from "components/Dashboard/TrendChart/TrendChart";
import { Badge, Box, Tooltip, Typography, useMediaQuery } from "@mui/material";
import CardWithControls from "components/Dashboard/CardWithControls/CardWithControls";
import { useMemo, useTransition } from "react";
import styled, { css } from "styled-components/macro";
import TrendChartCardControls from "components/Dashboard/TrendChart/TrendChartCardControls/TrendChartCardControls";
import {
  TREND_CHART_CARD_ID,
  useIsTrendChartExpandedTimelinePage,
} from "../../../dashboardPagesUtils";
import { PageNameEnum } from "types/enums";
import {
  LINE_ROLE_ICONS,
  useLeaderConstraintProcessIds,
} from "components/Dashboard/Tiles/ProcessTile/LineRoleDecorator";
import { useProcessesInVisibleGroups } from "utils/customHooks/genericHooks";
import {
  lightAccentSuccess10,
  lightAccentSuccess50,
  lightAccentSuccess90,
} from "constants/colors";

const ProcessIndicatorContainer = styled(Box)`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 8px;
  transform: scale(0.8);
  transform-origin: top right;
  .MuiBadge-badge {
    top: 10px !important;
    left: -10px !important;
    svg {
      transform: scale(0.8);
      color: ${lightAccentSuccess90};
      background: ${lightAccentSuccess10};
      border: 1px solid ${lightAccentSuccess50};
      border-radius: 4px;
    }
  }

  .process-indicator {
    display: flex;
    align-items: center;
    margin-left: 12px;
  }
`;

export function TrendChartCard() {
  const isPrint = useMediaQuery("print");
  const { translate } = useFlightTranslation();

  const [isTrendChartExpanded, setIsTrendChartExpanded] =
    useIsTrendChartExpandedTimelinePage();

  // Get the leader/constraint process IDs
  const { leaderProcessId, constraintProcessIds } =
    useLeaderConstraintProcessIds();

  // Get all processes to find names by ID
  const allProcesses = useProcessesInVisibleGroups();

  // Find leader and constraint processes
  const leaderProcess = useMemo(() => {
    return leaderProcessId
      ? allProcesses.find(p => p.id === leaderProcessId)
      : null;
  }, [leaderProcessId, allProcesses]);

  const constraintProcesses = useMemo(() => {
    if (!constraintProcessIds?.length) {
      return [];
    }
    return allProcesses.filter(p => constraintProcessIds.includes(p.id));
  }, [constraintProcessIds, allProcesses]);

  // Check if there is a leader or constraint process
  const hasLeader = Boolean(leaderProcess);
  const hasConstraint = Boolean(constraintProcesses.length);
  const isTrendChartExpandedLS = [null, "true"].includes(
    window.localStorage.getItem(
      `collapsibles:${PageNameEnum.timeline}:${TREND_CHART_CARD_ID}`
    )
  );

  const [isPending, startTransition] = useTransition();

  return (
    <>
      <CardWithControls
        {...DASHBOARD_CARD_PROPS}
        title={translate("Group_Trend")}
        id={TREND_CHART_CARD_ID}
        className={`Card_TrendChart${
          isTrendChartExpanded || isTrendChartExpandedLS
            ? " break-after expanded"
            : ""
        } Card_TrendChart_TimelinePage`}
        customCss={css`
          position: relative;
          .cardContent {
            overflow: ${isPrint ? "visible" : "hidden"};
            height: ${isPrint ? 600 : 500}px;
            margin-top: 10px;
          }
          ${isPending ? "opacity: 0.5;" : ""}
        `}
        CardConfigControls={<TrendChartCardControls />}
        onCollapse={nextExpanded => {
          startTransition(() => {
            setIsTrendChartExpanded(nextExpanded);
          });
        }}
        forceCollapsed={!isTrendChartExpanded}
      >
        {/* Leader/Constraint process indicators */}
        {(hasLeader || hasConstraint) && (
          <ProcessIndicatorContainer>
            {hasLeader && leaderProcess && (
              <div className="process-indicator">
                <Tooltip title={translate("Leader_Process")} disableInteractive>
                  <Badge
                    badgeContent={LINE_ROLE_ICONS.LEADER}
                    anchorOrigin={{ horizontal: "left", vertical: "top" }}
                    className="machineNameContainerWithBadge"
                  >
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ fontWeight: 500, pl: 1 }}
                    >
                      {leaderProcess.name}
                    </Typography>
                  </Badge>
                </Tooltip>
              </div>
            )}
            {hasConstraint &&
              constraintProcesses.map(process => (
                <div
                  className="process-indicator"
                  key={`constraint-${process.id}`}
                >
                  <Tooltip
                    title={translate("Constraint_Process")}
                    disableInteractive
                  >
                    <Badge
                      badgeContent={LINE_ROLE_ICONS.CONSTRAINT}
                      anchorOrigin={{ horizontal: "left", vertical: "top" }}
                      className="machineNameContainerWithBadge"
                    >
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ fontWeight: 500, pl: 1 }}
                      >
                        {process.name}
                      </Typography>
                    </Badge>
                  </Tooltip>
                </div>
              ))}
          </ProcessIndicatorContainer>
        )}
        <TrendChart />
      </CardWithControls>
      <PnlChartPopup />
    </>
  );
}
